import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Compass, SignOut, ShieldStar, UserCircle, List, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { to: "/", label: "Shipyard" },
    { to: "/catalog", label: "Arsenal" },
    { to: "/blueprint", label: "Blueprint Engine" },
  ];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 surface-glass border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <div className="w-9 h-9 border border-[#FFD700] rotate-45 flex items-center justify-center group-hover:animate-pulse-gold transition-all">
            <ShieldStar size={18} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="heading-display text-base text-white">EIGOO</span>
            <span className="overline text-[0.55rem] tracking-[0.3em]">Provisions</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-[#FFD700]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user && user !== false ? (
            <>
              <Link
                to="/commander"
                data-testid="nav-commander-link"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-white/80 hover:text-[#FFD700]"
              >
                <UserCircle size={20} weight="duotone" />
                {user.name}
              </Link>
              <Button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                variant="ghost"
                className="btn-ghost px-4 py-2 text-xs h-auto"
              >
                <SignOut size={16} className="mr-2" />
                Disengage
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70 hover:text-white"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-link"
                className="btn-gold px-5 py-2.5 text-xs"
              >
                Commission Account
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 surface-glass" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block text-sm uppercase tracking-[0.2em] font-semibold text-white/80"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/5 space-y-3">
              {user && user !== false ? (
                <>
                  <Link to="/commander" onClick={() => setOpen(false)} className="block btn-ghost px-4 py-2.5 text-xs text-center">Commander Deck</Link>
                  <button
                    onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                    className="w-full btn-ghost px-4 py-2.5 text-xs"
                  >
                    Disengage
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="block btn-ghost px-4 py-2.5 text-xs text-center">Sign In</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="block btn-gold px-4 py-2.5 text-xs text-center">Commission Account</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
